/* ==========================================================================
   HYA accessibility layer 2 — Level Access manual evaluation remediation
   (WCAG 2.2 A/AA), October 2026. Runs after theme.js + hya-a11y.js.

   Modules: live announcer, main navigation disclosure menus, header search
   dialog, sort menus, product-card swatches / "+N more", quick buy, owl
   carousels, slideshow pause + announcements, cart status, modal dialogs
   (size guide, gallery zoom, "just added"), collection filters, content
   clean-ups.
   ========================================================================== */
(function () {
  'use strict';

  var S = window.HYA_A11Y_STRINGS || {};
  var $ = window.jQuery;
  var DESKTOP = 768;

  /* ---------------------------------------------------------------------- */
  /* helpers                                                                */
  /* ---------------------------------------------------------------------- */
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function txt(el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }
  function decode(str) {
    if (!str || str.indexOf('&') === -1) return str || '';
    var ta = document.createElement('textarea'); ta.innerHTML = str; return ta.value;
  }
  function fmt(str, map) {
    str = decode(str || '');
    Object.keys(map || {}).forEach(function (k) { str = str.split('__' + k + '__').join(map[k]); });
    return str;
  }
  function isDesktop() { return window.innerWidth >= DESKTOP; }
  function visible(el) {
    if (!el || !el.getClientRects().length) return false;
    var cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  }
  var liveTimer;
  function live(msg, assertive) {
    var el = document.getElementById(assertive ? 'hya-live-assertive' : 'hya-live');
    if (!el || !msg) return;
    clearTimeout(liveTimer);
    el.textContent = '';
    liveTimer = setTimeout(function () { el.textContent = msg; }, 60);
  }
  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
  function focusables(root) {
    return qsa(FOCUSABLE, root).filter(function (el) {
      return visible(el) && !el.closest('[inert]') && !el.closest('[aria-hidden="true"]');
    });
  }
  function trapTab(e, container) {
    if (e.key !== 'Tab') return;
    var items = focusables(container);
    if (!items.length) { e.preventDefault(); return; }
    var first = items[0], last = items[items.length - 1], active = document.activeElement;
    if (e.shiftKey && (active === first || !container.contains(active))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (active === last || !container.contains(active))) { e.preventDefault(); first.focus(); }
  }
  /* make everything outside `el` inert; returns a restore function */
  function inertOthers(el) {
    var touched = [];
    var node = el;
    while (node && node !== document.body) {
      var parent = node.parentElement;
      if (!parent) break;
      Array.prototype.forEach.call(parent.children, function (sib) {
        if (sib !== node && !/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(sib.tagName) && !sib.hasAttribute('inert') && sib.id !== 'hya-live' && sib.id !== 'hya-live-assertive') {
          sib.setAttribute('inert', '');
          sib.setAttribute('data-hya-inert', '');
          touched.push(sib);
        }
      });
      node = parent;
    }
    return function () {
      touched.forEach(function (n) { n.removeAttribute('inert'); n.removeAttribute('data-hya-inert'); });
    };
  }
  function onClassChange(callback) {
    if (!('MutationObserver' in window)) return;
    new MutationObserver(function (records) {
      records.forEach(function (r) { if (r.type === 'attributes' && r.target.nodeType === 1) callback(r.target, r.oldValue || ''); });
    }).observe(document.body, { attributes: true, attributeFilter: ['class'], attributeOldValue: true, subtree: true });
  }
  function onAdded(callback) {
    if (!('MutationObserver' in window)) return;
    new MutationObserver(function (records) {
      records.forEach(function (r) {
        Array.prototype.forEach.call(r.addedNodes, function (n) { if (n.nodeType === 1) callback(n, 'added'); });
        Array.prototype.forEach.call(r.removedNodes, function (n) { if (n.nodeType === 1) callback(n, 'removed'); });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------- */
  /* 1. Main navigation — disclosure submenus (E37, E40, E42, E43)           */
  /* ---------------------------------------------------------------------- */
  function initNav() {
    var mainNav = qs('#main-nav .navigation--main');
    if (!mainNav) return;

    function mainItems() { return qsa('.navigation__tier-1 > .navigation__item', mainNav); }
    function proxyItems() { var p = qs('.navigation--left'); return p ? qsa('.navigation__tier-1 > .navigation__item', p) : []; }
    function itemIndex(item) { return Array.prototype.indexOf.call(item.parentElement.children, item); }
    function mainItemFor(toggle) {
      var item = toggle.parentElement;
      if (mainNav.contains(item)) return item;
      return mainItems()[itemIndex(item)] || null;
    }
    function togglesFor(mainItem) {
      var i = itemIndex(mainItem), list = [];
      var t1 = qs(':scope > .navigation__children-toggle', mainItem);
      if (t1) list.push(t1);
      var p = proxyItems()[i];
      var t2 = p && qs(':scope > .navigation__children-toggle', p);
      if (t2) list.push(t2);
      return list;
    }
    function setExpanded(mainItem, open) {
      togglesFor(mainItem).forEach(function (t) { t.setAttribute('aria-expanded', open ? 'true' : 'false'); });
    }
    function closeSub(mainItem, focusToggle) {
      if (!mainItem) return;
      mainItem.classList.remove('hya-submenu-open', 'navigation__item--show-children');
      setExpanded(mainItem, false);
      var opener = mainItem.__hyaOpener;
      mainItem.__hyaOpener = null;
      if (focusToggle) {
        var target = opener && visible(opener) ? opener : togglesFor(mainItem).filter(visible)[0] || togglesFor(mainItem)[0];
        if (target) target.focus();
      }
    }
    function closeAll(except) {
      mainItems().forEach(function (it) { if (it !== except && it.classList.contains('hya-submenu-open')) closeSub(it, false); });
    }
    function openSub(mainItem, toggle) {
      closeAll(mainItem);
      mainItem.__hyaOpener = toggle;
      mainItem.classList.add('hya-submenu-open', 'navigation__item--show-children');
      setExpanded(mainItem, true);
      setTimeout(function () {
        var panel = qs('.navigation__tier-2-container', mainItem);
        var first = panel ? focusables(panel)[0] : null;
        if (first) first.focus();
      }, 60);
    }
    function handleToggle(toggle) {
      var mainItem = mainItemFor(toggle);
      if (!mainItem) return;
      if (mainItem.classList.contains('hya-submenu-open') || mainItem.classList.contains('navigation__item--show-children')) {
        closeSub(mainItem, true);
      } else {
        openSub(mainItem, toggle);
      }
    }
    function isDesktopToggle(el) {
      return el && el.matches('.navigation__tier-1 > .navigation__item > .navigation__children-toggle') && !el.closest('.mobile-navigation-drawer') && isDesktop();
    }

    /* Enter / Space / click on a tier-1 toggle (capture phase so the theme's own
       Enter handler, which only mirrors hover, does not double-toggle). */
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if (!isDesktopToggle(t)) return;
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault(); e.stopPropagation();
        handleToggle(t);
      }
    }, true);
    document.addEventListener('click', function (e) {
      var t = e.target.closest('.navigation__children-toggle');
      if (!isDesktopToggle(t)) return;
      if (e.detail === 0 && e.__hyaHandled) return;
      e.preventDefault(); e.stopPropagation();
      handleToggle(t);
    }, true);

    /* Escape closes an open submenu and returns focus to its toggle */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !isDesktop()) return;
      var inSub = e.target.closest('#main-nav .navigation__tier-1 > .navigation__item--with-children');
      var onToggle = e.target.closest('.navigation__children-toggle');
      var mainItem = inSub || (onToggle && mainItemFor(onToggle));
      if (mainItem && (mainItem.classList.contains('hya-submenu-open') || mainItem.classList.contains('navigation__item--show-children'))) {
        e.preventDefault();
        closeSub(mainItem, true);
      }
    });

    /* Focus leaving an open submenu closes it */
    mainNav.addEventListener('focusout', function (e) {
      if (!isDesktop()) return;
      var item = e.target.closest('.navigation__tier-1 > .navigation__item--with-children');
      if (!item || !item.classList.contains('hya-submenu-open')) return;
      var to = e.relatedTarget;
      if (to && (item.contains(to) || togglesFor(item).indexOf(to) > -1)) return;
      setTimeout(function () {
        var a = document.activeElement;
        if (a && (item.contains(a) || togglesFor(item).indexOf(a) > -1)) return;
        closeSub(item, false);
      }, 10);
    });
    /* Mouse leaving a keyboard-opened submenu hands control back to the theme */
    mainNav.addEventListener('mouseleave', function () {
      mainItems().forEach(function (it) {
        if (it.classList.contains('hya-submenu-open') && !it.contains(document.activeElement)) it.classList.remove('hya-submenu-open');
      });
    }, true);

    /* Keep aria-expanded in sync with hover-driven state and the mobile drawer */
    onClassChange(function (el, oldCls) {
      if (!el.classList.contains('navigation__item')) return;
      if (el.closest('.mobile-navigation-drawer')) {
        var open = el.classList.contains('navigation__item--open');
        var t = qs(':scope > .navigation__children-toggle', el);
        if (t) t.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }
      if (mainNav.contains(el) && el.parentElement.classList.contains('navigation__tier-1')) {
        var now = el.classList.contains('navigation__item--show-children');
        var was = /navigation__item--show-children/.test(oldCls);
        if (now !== was) {
          setExpanded(el, now);
          if (!now) el.classList.remove('hya-submenu-open');
        }
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* 2. Header search dialog (E28, E67, E142, E65, E25, E86, E189)          */
  /* ---------------------------------------------------------------------- */
  function initSearch() {
    var search = document.getElementById('main-search');
    if (!search) return;
    var container = qs('.main-search__container', search) || search;
    var status = document.getElementById('main-search-status');
    var results = qs('.main-search__results', search);
    var restore = null;

    function opened() { restore = inertOthers(search); }
    function closed() {
      if (restore) { restore(); restore = null; }
      setTimeout(function () {
        if (!document.activeElement || document.activeElement === document.body) {
          var l = qs('.show-search-link'); if (l) l.focus();
        }
      }, 0);
    }
    new MutationObserver(function () {
      var open = document.body.classList.contains('show-search');
      if (open && !restore) opened();
      else if (!open && restore) closed();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    search.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        /* the theme closes on keyup from the input itself; handle every other element */
        if (e.target.classList.contains('main-search__input')) return;
        e.preventDefault();
        var c = qs('.main-search__close', search); if (c) c.click();
      } else if (e.key === 'Tab') {
        trapTab(e, container);
      }
    });

    if (results) {
      var t;
      new MutationObserver(function () { clearTimeout(t); t = setTimeout(announce, 350); }).observe(results, { childList: true, subtree: true });
    }
    function announce() {
      qsa('h6.main-search-result__heading', results).forEach(function (h) {
        var h2 = document.createElement('h2');
        h2.className = h.className + ' h6-style';
        h2.innerHTML = h.innerHTML;
        h.parentNode.replaceChild(h2, h);
      });
      if (!status) return;
      var n = qsa('.main-search-result', results).length, msg = '';
      if (search.classList.contains('main-search--no-results')) msg = decode(S.no_results) || 'No results';
      else if (n === 1) msg = decode(S.one_result_available) || '1 result available';
      else if (n > 1) msg = fmt(S.results_available || '__COUNT__ results available', { COUNT: n });
      if (msg && msg !== status.textContent) { status.textContent = ''; setTimeout(function () { status.textContent = msg; }, 60); }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* 3. Sort / filter link-dropdown menus (E38, E48)                         */
  /* ---------------------------------------------------------------------- */
  function initLinkDropdowns() {
    document.addEventListener('keydown', function (e) {
      var dd = e.target.closest('.link-dropdown');
      if (!dd) return;
      var btn = qs('.link-dropdown__button', dd);
      if (!btn) return;
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      var links = qsa('.link-dropdown__options a', dd);
      if (e.key === 'Escape') {
        if (expanded) { e.preventDefault(); e.stopPropagation(); btn.click(); btn.focus(); }
        return;
      }
      if (!expanded) {
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && e.target === btn) {
          e.preventDefault(); btn.click();
          setTimeout(function () { var f = qsa('.link-dropdown__options a', dd).filter(visible)[0]; if (f) f.focus(); }, 50);
        }
        return;
      }
      links = links.filter(visible);
      if (!links.length) return;
      var i = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (links[i + 1] || links[0]).focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (links[i - 1] || links[links.length - 1]).focus(); }
      else if (e.key === 'Home') { e.preventDefault(); links[0].focus(); }
      else if (e.key === 'End') { e.preventDefault(); links[links.length - 1].focus(); }
      else if (e.key === 'Tab') {
        /* leaving the menu closes it */
        setTimeout(function () { if (!dd.contains(document.activeElement) && btn.getAttribute('aria-expanded') === 'true') btn.click(); }, 0);
      }
    });
    document.addEventListener('focusout', function (e) {
      var dd = e.target.closest('.link-dropdown');
      if (!dd) return;
      var btn = qs('.link-dropdown__button', dd);
      if (!btn || btn.getAttribute('aria-expanded') !== 'true') return;
      var to = e.relatedTarget;
      if (to && dd.contains(to)) return;
      setTimeout(function () { if (!dd.contains(document.activeElement) && btn.getAttribute('aria-expanded') === 'true') btn.click(); }, 0);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* 4. Product-card swatches as radios + "+N more" (E103, E105, E139 …)    */
  /* ---------------------------------------------------------------------- */
  function radiosIn(group) {
    return qsa('[role="radio"]', group).filter(function (r) { return getComputedStyle(r).display !== 'none'; });
  }
  function setupSwatchGroups(root) {
    qsa('.product-block-options__inner[role="radiogroup"]', root).forEach(function (g) {
      var items = radiosIn(g);
      if (!items.length) return;
      var checked = items.filter(function (r) { return r.getAttribute('aria-checked') === 'true'; })[0];
      var tabStop = checked || items[0];
      qsa('[role="radio"]', g).forEach(function (r) { r.tabIndex = (r === tabStop) ? 0 : -1; });
    });
  }
  function initSwatches() {
    document.addEventListener('click', function (e) {
      var r = e.target.closest('.product-block-options__item[role="radio"]');
      if (r) {
        var g = r.closest('[role="radiogroup"]');
        if (g) qsa('[role="radio"]', g).forEach(function (x) {
          x.setAttribute('aria-checked', x === r ? 'true' : 'false');
          x.tabIndex = x === r ? 0 : -1;
        });
        return;
      }
      var m = e.target.closest('.product-block-options__more-label');
      if (m) {
        e.preventDefault();
        var box = m.closest('.product-block-options');
        if (!box) return;
        box.classList.add('product-block-options--expanded');
        m.setAttribute('aria-expanded', 'true');
        var first = qs('.product-block-options__item--truncated', box);
        setupSwatchGroups(box);
        if (first) { first.tabIndex = 0; first.focus(); }
      }
    });
    document.addEventListener('keydown', function (e) {
      var r = e.target.closest('.product-block-options__item[role="radio"]');
      if (!r) return;
      var delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); r.click(); return; }
      if (!delta) return;
      e.preventDefault();
      var g = r.closest('[role="radiogroup"]');
      var items = radiosIn(g);
      var i = items.indexOf(r);
      var next = items[(i + delta + items.length) % items.length];
      if (next) { next.focus(); next.click(); }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* 5. Quick buy (E109, E146, E47, E102, E176)                              */
  /* ---------------------------------------------------------------------- */
  function initQuickbuy() {
    function containerFor(block) {
      var own = qs('.quickbuy-container', block);
      if (own) return own;
      var row = block.closest('.collection-slider-row');
      return row ? qs('.quickbuy-container', row) : null;
    }
    onClassChange(function (el, oldCls) {
      if (!el.classList.contains('product-block')) return;
      var now = el.classList.contains('expanded'), was = /\bexpanded\b/.test(oldCls);
      if (now === was) return;
      var toggle = qs('.quickbuy-toggle', el);
      if (toggle) toggle.setAttribute('aria-expanded', now ? 'true' : 'false');
      var cont = containerFor(el);
      if (!cont) return;
      if (now) {
        cont.removeAttribute('inert');
        cont.setAttribute('aria-hidden', 'false');
        var tries = 0;
        (function wait() {
          var qc = qs('.quickbuy-content', cont);
          if (qc) {
            qc.setAttribute('tabindex', '-1');
            qc.setAttribute('role', 'region');
            var title = txt(qs('.title', qc)) || txt(qs('.product-block__title', el)) || '';
            qc.setAttribute('aria-label', 'Quick buy' + (title ? ': ' + title : ''));
            setTimeout(function () { try { qc.focus({ preventScroll: true }); } catch (err) { qc.focus(); } }, 650);
          } else if (tries++ < 60) setTimeout(wait, 100);
        })();
      } else {
        var row = el.closest('.collection-slider-row');
        var stillOpen = row ? qs('.product-block.expanded', row) : null;
        if (stillOpen) return;
        if (cont.contains(document.activeElement) && toggle) toggle.focus();
        cont.setAttribute('inert', '');
        cont.setAttribute('aria-hidden', 'true');
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var cont = e.target.closest('.quickbuy-container');
      if (!cont || cont.hasAttribute('inert')) return;
      var close = qs('.close-detail', cont);
      if (close) { e.preventDefault(); close.click(); }
    });
    /* initial state: anything not expanded is inert */
    qsa('.quickbuy-container').forEach(function (c) {
      var block = c.closest('.product-block');
      var row = c.closest('.collection-slider-row');
      var open = (block && block.classList.contains('expanded')) || (row && qs('.product-block.expanded', row));
      if (!open) { c.setAttribute('inert', ''); c.setAttribute('aria-hidden', 'true'); }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* 6. Owl carousels (E19, E201, E175, E159, E164, E73 …)                  */
  /* ---------------------------------------------------------------------- */
  function updateOwl(car) {
    if (!car) return;
    qsa('.owl-item', car).forEach(function (it) {
      var active = it.classList.contains('active') && !it.classList.contains('cloned');
      if (active) { it.removeAttribute('inert'); it.removeAttribute('aria-hidden'); }
      else { it.setAttribute('inert', ''); it.setAttribute('aria-hidden', 'true'); }
    });
  }
  function initOwl() {
    var queued = [];
    function schedule(car) {
      if (!car || queued.indexOf(car) > -1) return;
      queued.push(car);
      setTimeout(function () { queued.splice(queued.indexOf(car), 1); updateOwl(car); }, 30);
    }
    onClassChange(function (el) {
      if (el.classList.contains('owl-item')) schedule(el.closest('.owl-carousel'));
      else if (el.classList.contains('owl-carousel') || el.classList.contains('owl-loaded')) schedule(el.closest('.owl-carousel') || el);
    });
    qsa('.owl-carousel').forEach(updateOwl);
    /* announce what is on screen after prev / next */
    document.addEventListener('click', function (e) {
      var b = e.target.closest('.collection-slider .has-paging .prev, .collection-slider .has-paging .next');
      if (!b) return;
      var slider = b.closest('.collection-slider');
      setTimeout(function () {
        var items = qsa('.owl-item:not(.cloned)', slider);
        var act = items.filter(function (i) { return i.classList.contains('active'); });
        if (!items.length || !act.length) return;
        var first = items.indexOf(act[0]) + 1, last = items.indexOf(act[act.length - 1]) + 1;
        var names = act.map(function (i) { return txt(qs('.product-block__title', i)); }).filter(Boolean);
        live('Showing products ' + first + ' to ' + last + ' of ' + items.length + (names.length ? ': ' + names.join(', ') : ''));
      }, 700);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* 7. Slideshow: pause/play + slide announcements (E61, E77)              */
  /* ---------------------------------------------------------------------- */
  function initSlideshow() {
    qsa('[data-slideshow-pause]').forEach(function (btn) {
      var wrap = btn.closest('.slideshow-a11y-wrap');
      var ss = wrap && qs('.slideshow', wrap);
      if (!ss) return;
      function call(method) { try { if ($ && $(ss).hasClass('slick-initialized')) $(ss).slick(method); } catch (err) {} }
      btn.addEventListener('click', function () {
        var paused = btn.getAttribute('aria-pressed') === 'true';
        if (paused) { call('slickPlay'); } else { call('slickPause'); }
        btn.setAttribute('aria-pressed', paused ? 'false' : 'true');
        btn.setAttribute('aria-label', paused ? btn.getAttribute('data-label-pause') : btn.getAttribute('data-label-play'));
        live(paused ? 'Slideshow playing' : 'Slideshow paused');
      });
      /* keep the slideshow still while keyboard focus is inside it */
      wrap.addEventListener('focusin', function () { call('slickPause'); });
      wrap.addEventListener('focusout', function (e) {
        if (wrap.contains(e.relatedTarget)) return;
        if (btn.getAttribute('aria-pressed') !== 'true') call('slickPlay');
      });
    });
    if (!$) return;
    var userAt = 0;
    document.addEventListener('click', function (e) {
      if (e.target.closest('.slideshow .slick-arrow, .slideshow .slick-dots, .slideshow-a11y-wrap .slick-arrow, .slideshow-a11y-wrap .slick-dots')) userAt = Date.now();
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.target.closest('.slideshow, .slideshow-a11y-wrap') && /^Arrow/.test(e.key)) userAt = Date.now();
    }, true);
    $(document).on('afterChange', '.slideshow', function (ev, slick, current) {
      if (Date.now() - userAt > 2000) return;
      var slide = slick && slick.$slides && slick.$slides.get(current);
      if (!slide) return;
      var label = slide.getAttribute('aria-label') || fmt(S.slide_of || 'Slide __INDEX__ of __TOTAL__', { INDEX: current + 1, TOTAL: slick.slideCount });
      live(label);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* 8. Cart (E4, E18, E20, E22, E23, E192)                                  */
  /* ---------------------------------------------------------------------- */
  function initCart() {
    var cart = qs('[data-section-type="cart-template"]');
    if (!cart) return;
    document.addEventListener('click', function (e) {
      var b = e.target.closest('button[data-toggle-shipping]');
      if (b) {
        setTimeout(function () {
          var calc = document.getElementById('shipping-calculator');
          var open = calc && getComputedStyle(calc).display !== 'none';
          b.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) { var c = qs('#address_country', calc); if (c) c.focus(); }
        }, 60);
      }
      var q = e.target.closest('.quantity-down, .quantity-up');
      if (q && q.id) lastQty = q.id;
    }, true);
    var changed = 0, lastQty = null;
    document.addEventListener('theme:cartchanged', function () { changed = Date.now(); });
    var t;
    new MutationObserver(function () {
      if (Date.now() - changed > 5000) return;
      clearTimeout(t);
      t = setTimeout(function () {
        var st = qs('.subtotal', cart);
        var sub = txt(st).replace(/^[^$€£\d]*/, '');
        live(fmt(S.cart_updated || 'Cart updated. Subtotal __SUBTOTAL__', { SUBTOTAL: sub }));
        if (lastQty && (!document.activeElement || document.activeElement === document.body)) {
          var again = document.getElementById(lastQty);
          if (again && visible(again)) again.focus();
          else {
            var wrap = again && again.closest('.quantity');
            var inp = wrap ? qs('input', wrap) : qs('.cart-item__quantity-input', cart);
            if (inp) inp.focus();
          }
        }
      }, 400);
    }).observe(cart, { childList: true, subtree: true });

    var wr = document.getElementById('wrapper-response');
    if (wr) {
      new MutationObserver(function () {
        var fb = document.getElementById('shipping-rates-feedback');
        var zip = document.getElementById('address_zip');
        if (fb && fb.classList.contains('error')) {
          if (zip) { zip.setAttribute('aria-invalid', 'true'); zip.focus(); }
        } else if (zip) {
          zip.removeAttribute('aria-invalid');
        }
      }).observe(wr, { childList: true, subtree: true });
    }
  }

  /* ---------------------------------------------------------------------- */
  /* 9. Dialogs: size guide (cc-modal), gallery zoom, "just added"          */
  /* ---------------------------------------------------------------------- */
  function initDialogs() {
    var lastModalOpener = null, lastGalleryOpener = null;
    document.addEventListener('click', function (e) {
      var m = e.target.closest('.cc-modal'); if (m) lastModalOpener = m;
      var g = e.target.closest('a.show-gallery, .gallery .thumbnail, .main-image a'); if (g) lastGalleryOpener = g;
    }, true);

    var restores = new WeakMap();
    function setupModal(win) {
      var fg = qs('.cc-modal-window__foreground', win) || win;
      if (!fg.getAttribute('aria-label') && !fg.getAttribute('aria-labelledby')) {
        var h = qs('h1, h2, h3, h4, .title', fg);
        var name = (lastModalOpener && txt(lastModalOpener)) || txt(h) || 'Dialog';
        fg.setAttribute('aria-label', name.slice(0, 80));
      }
      restores.set(win, inertOthers(win));
      win.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); var c = qs('.cc-modal-window__close', win); if (c) c.click(); }
        else trapTab(e, fg);
      });
    }
    function teardownModal(win) {
      var r = restores.get(win); if (r) { r(); restores.delete(win); }
    }
    function setupGallery(gv) {
      gv.setAttribute('role', 'dialog');
      gv.setAttribute('aria-modal', 'true');
      var h1 = qs('h1');
      gv.setAttribute('aria-label', (txt(h1) ? txt(h1) + ' – ' : '') + 'image gallery');
      var close = qs('.gallery-viewer__close', gv), prev = qs('.gallery-viewer__prev', gv), next = qs('.gallery-viewer__next', gv);
      if (close) close.setAttribute('aria-label', S.close || 'Close');
      if (prev) prev.setAttribute('aria-label', 'Previous image');
      if (next) next.setAttribute('aria-label', 'Next image');
      var thumbs = qsa('.gallery-viewer__thumb', gv);
      thumbs.forEach(function (t, i) { t.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + thumbs.length); });
      var zoom = qs('.gallery-viewer__zoom-container', gv);
      if (zoom) { zoom.setAttribute('role', 'img'); zoom.setAttribute('aria-label', 'Zoomed product image. Click to toggle zoom.'); }
      restores.set(gv, inertOthers(gv));
      setTimeout(function () {
        var active = qs('.gallery-viewer__thumb--active', gv);
        var target = (active && visible(active)) ? active : close;
        if (target) target.focus();
      }, 80);
      gv.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); if (close) close.click(); }
        else if (e.key === 'ArrowRight') { if (next) next.click(); }
        else if (e.key === 'ArrowLeft') { if (prev) prev.click(); }
        else trapTab(e, gv);
      });
    }
    function teardownGallery(gv) {
      var r = restores.get(gv); if (r) { r(); restores.delete(gv); }
      if (lastGalleryOpener && document.contains(lastGalleryOpener)) {
        var target = lastGalleryOpener;
        setTimeout(function () { try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); } }, 20);
      }
    }
    function setupAdded(ov) {
      ov.setAttribute('role', 'region');
      ov.setAttribute('aria-label', txt(qs('.cart-summary-overlay__column-title', ov)) || 'Just added');
      var title = txt(qs('.cart-summary-overlay__title', ov));
      var sub = txt(qs('.cart-summary-overlay__subtotal', ov)).replace(/^[^$€£\d]*/, '');
      live((S.added_to_cart || 'Added to cart') + (title ? ': ' + title : '') + (sub ? '. Subtotal ' + sub : ''), true);
      var more = qs('#shop-more', ov);
      if (more) { more.setAttribute('role', 'button'); more.setAttribute('aria-label', txt(more) + ' – ' + (S.close || 'Close')); }
      ov.addEventListener('keydown', function (e) { if (e.key === 'Escape' && more) { e.preventDefault(); more.click(); } });
    }

    onAdded(function (node, what) {
      if (what === 'added') {
        if (node.classList.contains('cc-modal-window')) setupModal(node);
        else if (node.classList.contains('gallery-viewer')) setupGallery(node);
        else if (node.id === 'cart-summary-overlay') setupAdded(node);
      } else {
        if (node.classList.contains('cc-modal-window')) teardownModal(node);
        else if (node.classList.contains('gallery-viewer')) teardownGallery(node);
      }
    });
    onClassChange(function (el, oldCls) {
      if (el.classList.contains('cc-modal-window') && el.classList.contains('cc-modal-window--closing') && !/--closing/.test(oldCls)) teardownModal(el);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* 10. Collection filters (E194, E198, E200)                                */
  /* ---------------------------------------------------------------------- */
  function initFilters() {
    function syncFilterToggles() {
      var fc = qs('.filter-container');
      if (!fc) return;
      var open = isDesktop() ? fc.classList.contains('filter-container--show-filters-desktop') : fc.classList.contains('filter-container--show-filters-mobile');
      qsa('[data-toggle-filters][aria-expanded]').forEach(function (t) { t.setAttribute('aria-expanded', open ? 'true' : 'false'); });
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-toggle-filters]')) setTimeout(syncFilterToggles, 350);
    });
    onClassChange(function (el, oldCls) {
      if (el.classList.contains('filter-group__heading')) {
        var hidden = el.classList.contains('toggle-target-toggler--is-hidden');
        el.setAttribute('aria-expanded', hidden ? 'false' : 'true');
      }
    });
    /* announce the product count after AJAX filtering / clearing */
    var section = qs('[data-section-type="collection-template"], [data-section-type="search-template"]');
    if (!section) return;
    var lastText = txt(qs('.utility-bar__item[role="status"]', section));
    var t;
    new MutationObserver(function (records) {
      var relevant = records.some(function (r) {
        return Array.prototype.some.call(r.addedNodes, function (n) {
          return n.nodeType === 1 && (n.matches('.product-list, .collection-listing, .utility-bar, .filters, .filter-container, [data-section-type]') || n.querySelector && n.querySelector('.product-list, .utility-bar__item'));
        });
      });
      if (!relevant) return;
      clearTimeout(t);
      t = setTimeout(function () {
        var el = qs('.utility-bar__item[role="status"]', section) || qs('.utility-bar__item[role="status"]');
        var now = txt(el);
        if (now && now !== lastText) { lastText = now; live(now); }
        else if (now) { live(now); }
        setupSwatchGroups(section);
        syncFilterToggles();
      }, 500);
    }).observe(section, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------- */
  /* 11. Content clean-ups (E155, E107, E145, E152, E82, E174)               */
  /* ---------------------------------------------------------------------- */
  function cleanContent(root) {
    root = root || document;
    /* tabindex="0" on plain content (pasted editor markup) */
    qsa('.rte [tabindex="0"], .article-content [tabindex="0"], .page-content [tabindex="0"]', root).forEach(function (el) {
      if (el.matches('a, button, input, select, textarea, summary, iframe, video, audio, [contenteditable]')) return;
      if (el.getAttribute('role') && el.getAttribute('role') !== 'group') return;
      el.removeAttribute('tabindex');
      if (el.getAttribute('role') === 'group') el.removeAttribute('role');
    });
    /* empty paragraphs / lone <br> runs announced as "blank" */
    qsa('.rte p, .rte div', root).forEach(function (p) {
      if (p.getAttribute('aria-hidden') === 'true') return;
      if (txt(p).replace(/ /g, '').length === 0 && !p.querySelector('img, svg, video, iframe, input, button, a[href]')) p.setAttribute('aria-hidden', 'true');
    });
    qsa('.rte br + br', root).forEach(function (br) { br.setAttribute('aria-hidden', 'true'); });
    /* bold-only paragraphs used as section titles in pasted content — only in
       article/page bodies that have no real sub-headings at all */
    qsa('.rte p', root).forEach(function (p) {
      if (p.getAttribute('role')) return;
      var rte = p.closest('.rte');
      if (!rte || !rte.closest('[data-section-type="article-template"], [data-section-type="page-template"], .article-content, .page-content, main')) return;
      if (rte.querySelector('h2, h3, h4') || p.closest('li, blockquote, table')) return;
      var kids = Array.prototype.filter.call(p.childNodes, function (n) { return !(n.nodeType === 3 && !n.textContent.trim()); });
      if (kids.length !== 1 || kids[0].nodeType !== 1 || !/^(STRONG|B)$/.test(kids[0].tagName)) return;
      var t = txt(p);
      if (t.length < 3 || t.length > 90 || /[.!?]$/.test(t) || !p.nextElementSibling) return;
      if (/^(h[1-6])$/i.test(p.nextElementSibling.tagName)) return;
      var level = p.closest('article, .article, .article-content') ? 2 : 2;
      p.setAttribute('role', 'heading');
      p.setAttribute('aria-level', String(level));
    });
    /* adjacent lists split by the editor → merge into one */
    qsa('.rte ul + ul, .rte ol + ol', root).forEach(function (l) {
      var prev = l.previousElementSibling;
      if (!prev || prev.tagName !== l.tagName) return;
      while (l.firstChild) prev.appendChild(l.firstChild);
      l.parentNode.removeChild(l);
    });
    /* disabled slick arrows are invisible — keep them out of the tab order */
    qsa('.slick-arrow.slick-disabled', root).forEach(function (b) { b.setAttribute('tabindex', '-1'); b.setAttribute('aria-hidden', 'true'); });
    qsa('.slick-arrow:not(.slick-disabled)[aria-hidden="true"]', root).forEach(function (b) { b.removeAttribute('tabindex'); b.removeAttribute('aria-hidden'); });
  }

  /* ---------------------------------------------------------------------- */
  /* 12. Misc one-offs                                                       */
  /* ---------------------------------------------------------------------- */
  function misc() {
    /* product gallery zoom links open a dialog */
    qsa('a.show-gallery:not([aria-haspopup])').forEach(function (a) { a.setAttribute('aria-haspopup', 'dialog'); });
    /* Banner/slide links: name them from their visible caption so the overlay
       widget does not synthesise a label such as "Move to bottom, …" */
    qsa('a.image-overlay__image-link').forEach(function (a) {
      var cap = txt(qs('.overlay-text__text', a)) || txt(qs('.overlay-text', a));
      if (!cap) return;
      cap = cap.slice(0, 120);
      if (a.getAttribute('aria-label') !== cap) a.setAttribute('aria-label', cap);
    });
    /* JS-generated carousel arrows: descriptive names (E79, E121, E160) */
    qsa('.slick-product-prev').forEach(function (b) { b.setAttribute('aria-label', 'Previous image'); });
    qsa('.slick-product-next').forEach(function (b) { b.setAttribute('aria-label', 'Next image'); });
    qsa('.slick-prev').forEach(function (b) { b.setAttribute('aria-label', 'Previous slide'); });
    qsa('.slick-next').forEach(function (b) { b.setAttribute('aria-label', 'Next slide'); });
    qsa('.slick-arrow svg title, .gallery-viewer__button svg title, .has-paging svg title').forEach(function (t) { t.parentNode.removeChild(t); });
    qsa('.slick-arrow svg:not([aria-hidden]), .gallery-viewer__button svg:not([aria-hidden])').forEach(function (sv) { sv.setAttribute('aria-hidden', 'true'); sv.setAttribute('focusable', 'false'); });
    /* owl / slick clones must never be reachable */
    qsa('.owl-item.cloned:not([inert]), .slick-cloned:not([inert])').forEach(function (c) { c.setAttribute('inert', ''); c.setAttribute('aria-hidden', 'true'); });
  }

  /* ---------------------------------------------------------------------- */
  /* boot                                                                    */
  /* ---------------------------------------------------------------------- */
  function run() {
    try { setupSwatchGroups(document); } catch (e) {}
    try { cleanContent(document); } catch (e) {}
    try { misc(); } catch (e) {}
  }
  function patchThemeIcons() {
    var th = window.theme;
    if (!th) return;
    if (th.icons) Object.keys(th.icons).forEach(function (k) {
      var v = th.icons[k];
      if (typeof v !== 'string') return;
      v = v.replace(/<title>[^<]*<\/title>/g, '');
      if (v.indexOf('aria-hidden') === -1) v = v.replace('<svg', '<svg aria-hidden="true" focusable="false"');
      th.icons[k] = v;
    });
    if (th.strings) {
      if (th.strings.previous === 'Previous') th.strings.previous = 'Previous slide';
      if (th.strings.next === 'Next') th.strings.next = 'Next slide';
    }
  }
  function init() {
    try { patchThemeIcons(); } catch (e) {}
    try { initNav(); } catch (e) {}
    try { initSearch(); } catch (e) {}
    try { initLinkDropdowns(); } catch (e) {}
    try { initSwatches(); } catch (e) {}
    try { initQuickbuy(); } catch (e) {}
    try { initOwl(); } catch (e) {}
    try { initSlideshow(); } catch (e) {}
    try { initCart(); } catch (e) {}
    try { initDialogs(); } catch (e) {}
    try { initFilters(); } catch (e) {}
    run();
    [800, 2500, 6000].forEach(function (ms) { setTimeout(run, ms); });
    if ('MutationObserver' in window) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; run(); }, 400);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
